import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, Signal, signal } from '@angular/core';
import { Router } from '@angular/router';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { ProjectListBloc } from '@connect-services/client/blocs/project-list.bloc';
import {
    BroadcastChannelEvent,
    BroadcastChannelMessageEnum,
    BroadcastChannelNameEnum,
    BroadcastChannelSourceEnum,
} from '@connect-services/client/models/broadcast-channel-data.model';
import { ActionsEnum } from '@connect-services/client/models/panel.model';
import { RouteNameEnum, RoutePathEnum } from '@connect-services/client/models/routes.model';
import { BroadcastChannelService } from '@connect-services/client/services/broadcast-channel.service';
import { ContentLayoutService } from '@connect-services/client/services/content-layout.service';
import { HasPermissionsService, OperatorEnum } from '@connect-services/client/services/has-permissions.service';
import { PanelService } from '@connect-services/client/services/panel.service';
import { ProjectSettingsPermissionsService } from '@connect-services/client/services/project-settings-permissions.service';
import { RouterService } from '@connect-services/client/services/router.service';
import { SubscriptionService } from '@connect-services/client/services/subscription.service';
import { PermissionAliasEnum } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RessourcesModule],
    providers: [RouterService],
    selector: 'app-sidebar',
    styleUrl: './sidebar.component.scss',
    templateUrl: './sidebar.component.html',
})
export class SidebarComponent implements OnInit {
    public readonly ActionsEnum = ActionsEnum;
    public readonly currentRoute!: Signal<RouteNameEnum | undefined>;

    protected readonly canOpenSynoptic = computed(() => {
        return this.hasSynopticPermissions() && !this.broadcastChannelService.isSynopticOpenInExternalPage();
    });
    protected readonly contentLayoutService = inject(ContentLayoutService);
    protected readonly hasAiAccess = signal(true);
    protected readonly hasInterventionOption = signal(false);
    protected readonly hasInterventionPermissions = computed(() => {
        return (
            this.hasInterventionOption() &&
            this.hasPermissionsService.checkPermissions(
                [PermissionAliasEnum.READ_FIELD_INTERVENTION, PermissionAliasEnum.MANAGE_FIELD_INTERVENTION],
                OperatorEnum.OR,
            )
        );
    });
    protected readonly hasSettingsPermissions = computed(() => {
        return this.projectSettingsPermissionsService.canEnterSettings();
    });
    protected readonly hasStatisticPermissions = computed(() => {
        return this.hasPermissionsService.checkPermissions([PermissionAliasEnum.READ_STATISTIC]);
    });
    protected readonly hasSynopticOption = signal(false);

    protected readonly hasSynopticPermissions = computed(() => {
        return (
            this.hasSynopticOption() && this.hasPermissionsService.checkPermissions([PermissionAliasEnum.READ_SYNOPTIC])
        );
    });
    protected readonly panelService = inject(PanelService);
    protected readonly RouteNameEnum = RouteNameEnum;
    protected readonly RoutePathEnum = RoutePathEnum;

    private readonly broadcastChannel: BroadcastChannel = new BroadcastChannel(BroadcastChannelNameEnum.SYNOPTIC);
    private readonly broadcastChannelService = inject(BroadcastChannelService);
    private readonly hasPermissionsService = inject(HasPermissionsService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly projectListBloc = inject(ProjectListBloc);
    private readonly projectSettingsPermissionsService = inject(ProjectSettingsPermissionsService);
    private readonly router = inject(Router);
    private readonly routerService = inject(RouterService);
    private readonly subscriptionService = inject(SubscriptionService);

    public constructor() {
        this.currentRoute = this.routerService.getCurrentRoute();

        effect(() => {
            if (
                this.panelService.currentPanel() &&
                this.contentLayoutService.mapSizeType()!.class === 'map-full-screen'
            ) {
                this.panelService.setPanel(null);
            }
        });

        this.broadcastChannel.postMessage(<BroadcastChannelEvent<null>>{
            message: BroadcastChannelMessageEnum.CHECK_OPENED_WINDOW,
            source: BroadcastChannelSourceEnum.HOMEPAGE,
        });

        this.broadcastChannel.onmessage = (event: MessageEvent<BroadcastChannelEvent<null>>): void => {
            this.handleBroadcastChannelEvents(event);
        };
    }

    public async navigateToNewPage(actionEnum: ActionsEnum, routePathEnum?: RoutePathEnum): Promise<void> {
        try {
            const panelActionEnum = this.panelService.currentPanel() === actionEnum ? null : actionEnum;
            const routeName = panelActionEnum ? routePathEnum : '';
            const projectId = this.projectBloc.value()?.id ?? this.projectListBloc.value()!.items[0].id;

            const path = [RoutePathEnum.PROJECTS, projectId];
            if (routeName) {
                path.push(routeName);
            }

            await this.router.navigate(path, { onSameUrlNavigation: 'reload' });
        } catch {
            return;
        }
    }

    public async ngOnInit(): Promise<void> {
        const hasConniePermission = this.hasPermissionsService.checkPermissions(
            [PermissionAliasEnum.READ_CONNIE, PermissionAliasEnum.MANAGE_CONNIE],
            OperatorEnum.OR,
        );
        const subscription = await this.subscriptionService.getSubscription();

        this.hasSynopticOption.set(subscription.options.hasSynoptic);

        this.hasInterventionOption.set(!!subscription.maxConnectFieldLicense);

        this.hasAiAccess.set(subscription.options.hasConnie && hasConniePermission);
    }

    public openSynoptic(): void {
        this.broadcastChannelService.openSynopticInExternalWindow();
    }

    private handleBroadcastChannelEvents(event: MessageEvent<BroadcastChannelEvent<null>>): void {
        if (
            event.data.source === BroadcastChannelSourceEnum.GRAPH &&
            event.data.message &&
            event.data.message === BroadcastChannelMessageEnum.CHECK_OPENED_WINDOW
        ) {
            this.broadcastChannelService.isSynopticOpenInExternalPage.set(true);
        }

        if (
            (event.data.isClosing || event.data.message === BroadcastChannelMessageEnum.CLOSE) &&
            event.data.source !== BroadcastChannelSourceEnum.SYNOPTIC_PLAN
        ) {
            this.broadcastChannelService.isSynopticOpenInExternalPage.set(false);
        }

        if (event.data.source !== BroadcastChannelSourceEnum.GRAPH) {
            return;
        }

        if (event.data.isOpening && !event.data.message) {
            this.broadcastChannelService.openSelectedSynopticOnNewWindow(this.broadcastChannel);
            this.broadcastChannelService.externalOpenInProgress.set(false);
            this.broadcastChannelService.isSynopticOpenInExternalPage.set(true);
        }
    }
}
